import storeManager from '../../store/StoreManager';
import objectTypes from '../../constants/ObjectTypes';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import visitedQuestionsReader from '../../store/readHelpers/VisitedQuestionsReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import tocReader from './TocReader';
import responseService from '../../services/ResponseService';

const responseReader = {
    isAtLeastOneInteractionRepliedTo(contentRevisionId, { response } = storeManager.getState()) {
        if (!response.status) {
            return false;
        }
        const itemStatus = response.status[contentRevisionId];
        return !!itemStatus && itemStatus.atLeastOneInteractionRepliedTo;
    },

    areAllInteractionRepliedTo(contentRevisionId) {
        const { response } = storeManager.getState();
        if (!response.status) {
            return false;
        }
        const itemStatus = response.status[contentRevisionId];
        return !!itemStatus?.allDeclarationsRepliedTo;
    },

    /**
     * Returns the number of answered questions in all sections from the state, not counting section stimulus
     * @param {object} state
     * @returns {int}
     */
    getTotalNumberOfResponses(state = storeManager.getState()) {
        const questionKeys = Object.getOwnPropertyNames(state.response);
        let count = 0;

        questionKeys.forEach((questionKey) => {
            const contentRevisionId = parseInt(questionKey, 10);
            if (isNaN(contentRevisionId)) {
                return;
            }

            const question = assessmentReader.getQuestion(contentRevisionId);
            if (
                tocReader.isSectionStimulus(contentRevisionId) ||
                questionReader.isForm(question) ||
                questionReader.isContentBlock(question)
            ) {
                return;
            }
            let repliedTo = false;
            if (
                question && (question.objectType === objectTypes.ORAL)
            ) {
                repliedTo = responseReader.isQuestionAttempted(question);
            } else {
                repliedTo = this.isAtLeastOneInteractionRepliedTo(contentRevisionId, state);
            }

            if (repliedTo) {
                count++;
            }
        });

        return count;
    },

    /**
     * Gets the itemResult for a given question.
     *
     * @param contentRevisionId
     * @returns {object | undefined}
     */
    getItemResultForQuestion(contentRevisionId) {
        const state = storeManager.getState();

        return state.response[contentRevisionId];
    },

    /**
     * Returns true when there is a non empty user response for at least one of the interactions in the question.
     * For objectTypes.DOCUMENT it always returns true.
     * For objectTypes.ORAL it returns true if the question is visited, otherwise false.
     *
     * @param {object} question
     * @param {object} state
     * @returns {boolean}
     */
    isQuestionAttempted(question, state = storeManager.getState()) {
        const contentRevisionId = question.contentRevisionId;
        const isViewContext = sessionReader.isContextAnyViewDeliveryType();

        if (questionReader.isDocument(question) || questionReader.isContentBlock(question)) {
            return true;
        }

        if (questionReader.isOralQuestion(question)) {
            return isViewContext || visitedQuestionsReader.isQuestionVisited(contentRevisionId, state);
        }
        return this.isAtLeastOneInteractionRepliedTo(contentRevisionId, state);
    },

    /**
     * Returns true when there is no non empty user response for all interactions in the question.
     * For objectTypes.DOCUMENT it always returns true.
     * For objectTypes.ORAL it returns true if the question is visited, otherwise false.
     *
     * @param {object} question
     * @returns {boolean}
     */
    isQuestionDone(question) {
        if (!question) {
            console.error(`Question does not exist, responseReader.isQuestionDone will return false: ${question}`);
            return false;
        }

        const state = storeManager.getState();
        const contentRevisionId = question.contentRevisionId;
        const isViewContext = sessionReader.isContextAnyViewDeliveryType();

        if (questionReader.isDocument(question) || questionReader.isContentBlock(question)) {
            return true;
        }

        if (questionReader.isOralQuestion(question)) {
            return isViewContext || visitedQuestionsReader.isQuestionVisited(contentRevisionId, state);
        }
        return this.areAllInteractionRepliedTo(contentRevisionId, state);
    },

    getResultsFromResponses(originalResponses, { response } = storeManager.getState()) {
        const results = {};
        Object.keys(originalResponses).forEach((key) => {
            try {
                const intKey = parseInt(key, 10);
                const originalResponse = originalResponses[key];
                const parsedResponse = response[intKey];
                const { manualScore, score } = originalResponse;
                const { outcomeVariables } = parsedResponse;
                const resData = {
                    manualScore,
                    score,
                };

                if (outcomeVariables &&
                    outcomeVariables.FEEDBACK &&
                    outcomeVariables.FEEDBACK.value !== undefined) {
                    resData.autoFeedback = outcomeVariables.FEEDBACK.value;
                }

                results[intKey] = resData;
            } catch (error) {
                window.console.error(error);
            }
        });

        return results;
    },

    getResponses(state = storeManager.getState()) {
        return Object
            .entries(state.response)
            .filter(entry => entry[1].identifier !== undefined)
            .map(entry => entry[1]);
    },

    getResponsesForAssessmentRunId(assessmentRunId, state = storeManager.getState()) {
        return this
            .getResponses(state)
            .filter(response => response.assessmentRunId === assessmentRunId);
    },

    getFirstUncommittedQuestion() {
        const { toc: { itemsOrder } } = storeManager.getState();
        return itemsOrder.find((item) => {
            const questionResponse = this.getItemResultForQuestion(item.contentRevisionId);
            return responseService.isQuestionAnswerCommittingUnattempted(questionResponse);
        });
    },

    areAllQuestionsCommitted() {
        const { toc: { itemsOrder } } = storeManager.getState();
        return !!itemsOrder && itemsOrder.length > 0 && itemsOrder.every((item) => {
            const questionResponse = this.getItemResultForQuestion(item.contentRevisionId);
            // TEII-5241 - question responses that are either stored or pending will be considered as committed,
            // to avoid taking TT to previous answered questions.
            return responseService.isQuestionCommitAnswerStored(questionResponse) || responseService.isQuestionCommitAnswerPending(questionResponse);
        });
    },
};

export default responseReader;
